import { Command as CommandPrimitive } from 'cmdk'
import { CheckIcon, ChevronsUpDownIcon, SearchIcon, XIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import type { ComboboxOption } from './combobox'

/** Multi-select variant of `Combobox`: selected values render as removable chips. */
export function MultiCombobox({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyText = 'No results',
  id,
  disabled,
  className,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: {
  value: string[]
  onChange: (value: string[]) => void
  options: ComboboxOption[]
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  id?: string
  disabled?: boolean
  className?: string
  'aria-invalid'?: boolean
  'aria-describedby'?: string
}) {
  const [open, setOpen] = useState(false)
  const selected = useMemo(() => value.map((v) => options.find((o) => o.value === v) ?? { value: v, label: v }), [options, value])
  const toggle = (v: string) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
          disabled={disabled}
          className={cn(
            'flex min-h-9 w-full items-center justify-between gap-2 rounded-lg border border-input bg-card px-2 py-1 text-left text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-primary/60 focus-visible:ring-[3px] focus-visible:ring-ring/60 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive dark:bg-input/30',
            className,
          )}
        >
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
            {selected.length === 0 ? (
              <span className="px-1 text-muted-foreground">{placeholder}</span>
            ) : (
              selected.map((o) => (
                <span key={o.value} className="inline-flex max-w-[12rem] items-center gap-1 rounded-md border bg-muted/60 py-0.5 pr-0.5 pl-1.5 text-xs">
                  {o.prefix}
                  <span className="truncate">{o.label}</span>
                  <span
                    role="button"
                    tabIndex={-1}
                    aria-label={`Remove ${o.label}`}
                    className="rounded p-0.5 text-muted-foreground hover:bg-background hover:text-foreground"
                    onPointerDown={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      toggle(o.value)
                    }}
                  >
                    <XIcon className="size-3" />
                  </span>
                </span>
              ))
            )}
          </span>
          <ChevronsUpDownIcon className="size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) min-w-64 p-0" align="start">
        <CommandPrimitive className="flex flex-col">
          <div className="flex h-10 items-center gap-2 border-b px-3">
            <SearchIcon className="size-4 shrink-0 text-muted-foreground" />
            <CommandPrimitive.Input placeholder={searchPlaceholder} className="h-9 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground" />
          </div>
          <CommandPrimitive.List className="max-h-64 overflow-y-auto p-1 scrollbar-thin">
            <CommandPrimitive.Empty className="py-6 text-center text-sm text-muted-foreground">{emptyText}</CommandPrimitive.Empty>
            {options.map((option) => {
              const checked = value.includes(option.value)
              return (
                <CommandPrimitive.Item
                  key={option.value}
                  value={option.value}
                  keywords={[option.label, ...(option.keywords ?? [])]}
                  onSelect={() => toggle(option.value)}
                  className="flex cursor-default items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground"
                >
                  <span
                    className={cn(
                      'flex size-4 shrink-0 items-center justify-center rounded-[5px] border border-input',
                      checked && 'border-primary bg-primary text-primary-foreground',
                    )}
                  >
                    {checked && <CheckIcon className="size-3" strokeWidth={3} />}
                  </span>
                  {option.prefix}
                  <span className="truncate">{option.label}</span>
                  {option.hint && <span className="ml-auto shrink-0 pl-2 text-xs text-muted-foreground">{option.hint}</span>}
                </CommandPrimitive.Item>
              )
            })}
          </CommandPrimitive.List>
        </CommandPrimitive>
      </PopoverContent>
    </Popover>
  )
}
